import { Box, ListItem } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useCallback, useState } from 'react';
import palette from '@ui/palette';
import Typography from '@ui/Typography';
import AddButton from '@replacements/components/ReplacementsDictionary/AddButton';
import DictionarySuggestionPlayback, {
  AudioState,
} from '@replacements/components/ReplacementsDictionary/DictionarySuggestionPlayback';
import { ReplacementSuggestion } from '@replacements/types/replacementsDictionaryTypes';
import audioManager from 'lib/audio/StudioAudioManager';

interface DictionarySuggestionListItemProps extends ReplacementSuggestion {
  word: string;
  isAdded: boolean;
  onSelect: () => void;
  size: number;
  position: number;
  location: string;
}

const useStyles = makeStyles<{ showItem: boolean }>()(
  (theme, { showItem }) => ({
    addButton: {
      visibility: showItem ? 'visible' : 'hidden',
    },
    listItem: {
      gap: theme.spacing(1),
      wordBreak: 'break-all',
      borderRadius: theme.spacing(0.5),
      '&:hover': {
        backgroundColor: palette.gray_800,
      },
      '&:focus-visible': {
        outline: `2px solid ${palette.mint_200}`,
        outlineOffset: theme.spacing(-0.25),
        borderRadius: theme.spacing(0.5),
      },
    },
    gutters: {
      padding: theme.spacing(1, 3),
    },
    listPadding: {
      padding: 0,
    },
    playback: {
      padding: 0,
    },
    suggestionList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    suggestionsListContainer: {
      overflowY: 'scroll',
      overflowX: 'hidden',
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-track': {
        background: 'transparent',
      },
      '&::-webkit-scrollbar-thumb': {
        background: theme.palette.grey[700],
        borderRadius: '4px',
      },
    },
  })
);

const DictionarySuggestionListItem = (
  props: DictionarySuggestionListItemProps
) => {
  const { id, label, word, isAdded, onSelect, size, position, location } =
    props;

  const [isPlaying, setPlaying] = useState(false);
  const [isHovered, setHover] = useState(false);
  const { classes } = useStyles({
    showItem: isHovered || isPlaying,
  });

  const handleHover = (value: boolean) => () => setHover(value);
  const handleBlur: React.FocusEventHandler<HTMLDivElement> = useCallback(e => {
    const { currentTarget } = e;
    // Remove hover if blurred and not focused on a child element within list item
    requestAnimationFrame(() => {
      // Check if the new focused element is a child of the original container
      if (!currentTarget.contains(document.activeElement)) {
        setHover(false);
      }
    });
  }, []);

  const handleAudioPlayback = (state: AudioState) => {
    if (state === AudioState.playing) audioManager.pause();
    setPlaying(state === AudioState.requested || state === AudioState.playing);
  };

  return (
    <ListItem
      key={id}
      classes={{ gutters: classes.gutters, root: classes.listItem }}
      component={'div'}
      tabIndex={0}
      role={'listitem'}
      aria-label={label}
      aria-setsize={size}
      aria-posinset={position}
      onMouseEnter={handleHover(true)}
      onMouseLeave={handleHover(false)}
      onKeyUpCapture={handleHover(true)}
      onBlur={handleBlur}
    >
      <Typography variant="pSm">{label}</Typography>
      <Box ml={'auto'} display={'flex'} style={{ gap: 4 }}>
        <AddButton
          className={classes.addButton}
          isAdded={isAdded}
          onClick={onSelect}
          aria-label={`Add replacement`}
        />
        <DictionarySuggestionPlayback
          word={word}
          replacement={label}
          onAudioPlayback={handleAudioPlayback}
          location={location}
        />
      </Box>
    </ListItem>
  );
};

export default DictionarySuggestionListItem;
